import type { PaywallScenario, PaywallVariant } from "./paywallPersonalization";
import { resolveCheckoutProviderFromUrl, type CheckoutProvider } from "./checkoutProvider";

const RECOVERY_CODE_SESSION_KEY = "sehaj:checkout-recovery-code";

export type RecoveryContext = {
  planId: "weekly" | "monthly" | "quarterly" | "annual";
  checkoutProvider: CheckoutProvider;
  currency: string | null;
  paywallVariant: PaywallVariant;
  paywallScenario: PaywallScenario;
  lifecycleAttributionId: string;
  expiresAtUtc: string;
};

/**
 * Moves an opaque recovery code out of the URL before PostHog initializes.
 * The code remains only in sessionStorage until exchanged with the Worker.
 */
export const captureAndStripRecoveryCode = (
  urlLike: URL | string = window.location.href
): void => {
  if (typeof window === "undefined") return;
  const url = typeof urlLike === "string" ? new URL(urlLike) : new URL(urlLike.toString());
  const recoveryCode = url.searchParams.get("rid")?.trim();
  if (!recoveryCode) return;

  try {
    window.sessionStorage.setItem(RECOVERY_CODE_SESSION_KEY, recoveryCode);
  } catch {
    // The exchange will be unavailable if storage is blocked; still strip the code.
  }

  url.searchParams.delete("rid");
  window.history.replaceState(window.history.state, "", url);
};

export const readPendingRecoveryCode = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(RECOVERY_CODE_SESSION_KEY)?.trim() || null;
  } catch {
    return null;
  }
};

export const clearPendingRecoveryCode = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RECOVERY_CODE_SESSION_KEY);
  } catch {
    // Nothing else to clear.
  }
};

export const resolveLocalRecoveryPreview = (
  urlLike: URL | string = window.location.href
): RecoveryContext | null => {
  if (!import.meta.env.DEV) return null;
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const raw = url.searchParams.get("recovery_preview")?.trim();
  if (!raw) return null;
  const [plan, scenario = "starter_short"] = raw.split(":");
  if (plan !== "weekly" && plan !== "monthly" && plan !== "quarterly" && plan !== "annual") return null;
  const validScenarios = new Set(["default", "starter_short", "deeper_guided", "family_story"]);
  const paywallScenario = validScenarios.has(scenario) ? scenario as PaywallScenario : "default";
  return {
    planId: plan,
    checkoutProvider: resolveCheckoutProviderFromUrl(url),
    currency: "USD",
    paywallVariant: "personalized_14d",
    paywallScenario,
    lifecycleAttributionId: "local-review",
    expiresAtUtc: new Date(Date.now() + 60 * 60 * 1000).toISOString()
  };
};
